import React, {useState} from "react";
import {Alert, Avatar, Box, Button, Container, CssBaseline, TextField, Typography} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useLoginMutation} from "../../api/login/loginQueries";
import {useAuth} from "../auth/AuthContext";

const Login: React.FC = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const {t: translate} = useTranslation();
    const {completeLogin} = useAuth();
    const loginMutation = useLoginMutation();
    const isLoading = loginMutation.isPending;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError("");

        if (!username || !password) {
            setError(translate("login.fillAllFields"));
            return;
        }

        try {
            const secondsUntilLogout = await loginMutation.mutateAsync({username, password});
            await completeLogin(secondsUntilLogout);
            navigate("/");
        } catch (err) {
            console.error(err);
            setError(translate("login.loginFailed"));
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />

            <Box
                sx={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Avatar sx={{m: 1, bgcolor: "primary.main"}}>
                    <LockOutlinedIcon />
                </Avatar>

                <Typography component="h1" variant="h5">
                    {translate("login.signInTitle")}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{mt: 2, width: "100%"}}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{mt: 3}}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label={translate("login.usernameLabel")}
                        name="username"
                        type="text"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />

                    <TextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label={translate("login.passwordLabel")}
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{
                            mt: 3,
                            mb: 2,
                            minHeight: 56,
                            borderRadius: 1,
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? translate("login.signingIn") : translate("login.signIn")}
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default Login;
